import { type AchievementState } from '../../../modules/missions/types/state/achievements';
import {
  type AchievementReward,
  type AchievementEligibility,
  type AchievementClearMode,
  type AchievementEligiblePeriod,
  type AchievementBasketCondition,
} from '../../../modules/missions/types/state/achievements';
import { type EntitiesAchievements } from '../../../states/entities/achievements';
import { type StateType } from '../../../states/index';
import { type Selector } from '../../../types/selectors';
import { getAchievementIdFromProps } from '../../_propsGetters_';
import { selectEntitiesState } from '../entitiesPrimary.selector';

const emptyObject = {};
const emptyArray: [] = [];

export const selectEntitiesAchievements: Selector<EntitiesAchievements> = (state: StateType) =>
  selectEntitiesState(state).achievements ?? emptyObject;

//#region state.entities.achievements
type EntitiesAchievementsSelectorProps = {
  achievementId?: number;
};

export const selectEntitiesAchievementsEntity: Selector<AchievementState | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {},
) => {
  const achievementId = getAchievementIdFromProps(state, props);

  if (achievementId) {
    return selectEntitiesAchievements(state)[achievementId];
  }
};
//#endregion

//#region state.entities.achievements[achievementId]
export const selectEntitiesAchievementsEntityAchievementId: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntity(state, props)?.achievementId;

export const selectEntitiesAchievementsEntityMissionId: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntity(state, props)?.missionId;

export const selectEntitiesAchievementsEntityReward: Selector<AchievementReward | null | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntity(state, props)?.reward;

export const selectEntitiesAchievementsEntityDescription: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntity(state, props)?.description;

export const selectEntitiesAchievementsEntityEligibility: Selector<AchievementEligibility | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntity(state, props)?.eligibility;
//#endregion

//#region state.entities.achievements[achievementId].eligibility
export const selectEntitiesAchievementsEntityEligibilityMaxStartDate: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.maxStartDate;

export const selectEntitiesAchievementsEntityEligibilityClearMode: Selector<AchievementClearMode | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.clearMode;

export const selectEntitiesAchievementsEntityEligibilityMinBetOdds: Selector<number | undefined> = (
  state: StateType,
  props,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.minBetOdds;

export const selectEntitiesAchievementsEntityEligibilityMinPlacedAmount: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.minPlacedAmount;

export const selectEntitiesAchievementsEntityEligibilityNbBetsToPlace: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.nbBetsToPlace;

export const selectEntitiesAchievementsEntityEligibilityNbBetsToWin: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.nbBetsToWin;

export const selectEntitiesAchievementsEntityEligibilityBetCents: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.betCents;

export const selectEntitiesAchievementsEntityEligibilityEligiblePeriod: Selector<
  AchievementEligiblePeriod | null | undefined
> = (state: StateType, props: EntitiesAchievementsSelectorProps = {}) =>
  selectEntitiesAchievementsEntityEligibility(state, props)?.eligiblePeriod;

export const selectEntitiesAchievementsEntityEligibilityAllowedBetTypeCategories: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedBetTypeCategories ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityAllowedBetTypeIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedBetTypeIds ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityAllowedMatchIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedMatchIds ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityAllowedSportIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedSportIds ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityAllowedCategoryIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedCategoryIds ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityAllowedTournamentIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesAchievementsSelectorProps = {} as EntitiesAchievementsSelectorProps,
) => selectEntitiesAchievementsEntityEligibility(state, props)?.allowedTournamentIds ?? emptyArray;

export const selectEntitiesAchievementsEntityEligibilityBasketConditions: Selector<
  Array<AchievementBasketCondition> | undefined
> = (state: StateType, props: EntitiesAchievementsSelectorProps = {}) =>
  selectEntitiesAchievementsEntityEligibility(state, props)?.basketConditions;
//#endregion
